import { FunctionComponent, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from './redux/hooks';
import { setIsTriggerGetUser, setProfile } from './redux/slices/user/profileSlice';
import { GET_REALTOR_PROPERTY_PRESET, GET_USER } from './graphql/queries';
import useCustomQuery from './network/useCustomQuery';
import SuspenseLoader from './components/Loader/SuspenseLoader';
import { shouldGetProfileCheck } from './util/helpers/routeHelpers';
import { setRealtorPropertyPreset } from './redux/slices/properties/propertiesSlice';

export interface IAuthComp {
  children?: any;
}

const AuthGuard: FunctionComponent<IAuthComp> = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isTriggerGetUser, profile } = useSelector((state: any) => state?.userProfile);
  const { realtorPropertyPreset } = useSelector((state: any) => state?.properties);

  const handleError = () => {
    navigate('/');

    // To clear the current profile and its associated Redux store.
    window.location.reload();
  };

  const [getProfileQuery, profileLoading, profileData] = useCustomQuery({
    query: GET_USER,
    onError: (err: any) => {
      if (err) {
        if (
          err?.message?.toLowerCase?.() === 'sorry you have no access to this resource.' ||
          err?.message?.toLowerCase?.() === 'user not found'
        ) {
          handleError();
        }
      }
    },
    onData: () => {}
  });

  const [getRealtorPropertyPreset, , getRealtorPropertyPresetData] = useCustomQuery({
    query: GET_REALTOR_PROPERTY_PRESET,
    onError: () => {},
    onData: () => {
      dispatch(
        setRealtorPropertyPreset(getRealtorPropertyPresetData?.getRealtorPropertyPreset?.data)
      );
    }
  });

  const shouldGetProfile = shouldGetProfileCheck(
    [
      '/',
      '/otp-login',
      '/get-started',
      '/payment-success',
      '/forgot-password',
      '/get-verified',
      '/forgot-password-verify',
      '/password-reset',
      '/pay/rent/*'
    ],
    pathname
  );

  useEffect(() => {
    if (profileData?.getRealtorProfile) {
      const userProfile = {
        ...profileData?.getRealtorProfile?.data,
        isCompliant: profileData?.getRealtorProfile?.isCompliant,
        stripeLink: profileData?.getRealtorProfile?.stripeLink,
        trialPeriod: profileData?.getRealtorProfile?.trialPeriod,
        trialValid: profileData?.getRealtorProfile?.trialValid
      };

      dispatch(setProfile(userProfile));

      if (!realtorPropertyPreset?.propertyTypes?.length) {
        getRealtorPropertyPreset();
      }
    }
  }, [profileData]);

  // TODO: Change the below to Saga or RTK Listener.
  useEffect(() => {
    if ((shouldGetProfile && localStorage?.token) || isTriggerGetUser) {
      getProfileQuery();
    }
    return () => {
      dispatch(setIsTriggerGetUser(false));
    };
  }, [shouldGetProfile, localStorage?.token, isTriggerGetUser]);

  useEffect(() => {
    if (!localStorage?.token && shouldGetProfile) {
      handleError();
    }
  }, [shouldGetProfile, localStorage?.token, pathname, profileLoading]);

  if (profileLoading && !profile) {
    return <SuspenseLoader />;
  }

  // TODO: Add a fallback component when getting the profile fails.
  // if (!profileLoading && !profile) {
  //   return <div>something is up</div>;
  // }
  return children;
};

export default AuthGuard;
